import { LanguagesEnum } from '@/api/enums';
import {
  AccountAdminTask,
  AdminTask,
  AdminTaskPayload,
  AdminTaskRuleAccountStatus,
  AdminTaskRuleFilterBy,
  AdminTaskRuleOperator,
  AdminTaskRuleProduct,
  AdminTaskTagEnum,
  AdminTaskVisibilityGroupCondition,
  EditableTaskFormState,
} from '@/client/admin-tasks';
import { Tag } from '@/client/tags';
import { Subjects } from '@/client/users';
import { availableLanguagesDropdown } from '@/common/constants/languages';
import { TranslationFunctionType } from '@/common/types';
import { PostHogFeatureFlags } from '@/utils/posthog/posthog.events';

export type AdminTaskTagOption = {
  id: string;
  label: string;
  value: string;
};

export const taskToInitialValues = (
  task: AdminTask,
  language: LanguagesEnum = LanguagesEnum.EN,
): EditableTaskFormState => {
  const translationsMap: Record<
    string,
    { title: string; description: string; buttonName: string }
  > = {};
  task.translations?.forEach((tr) => {
    translationsMap[tr.language] = {
      title: tr.name,
      description: tr.description,
      buttonName: tr.buttonName,
    };
  });

  const currentTranslation = translationsMap[language] ?? {
    title: '',
    description: '',
    buttonName: '',
  };

  return {
    order: task.order ?? 0,
    taskId: task.id as string,
    active: true,
    taskLanguage: language,
    title: currentTranslation.title,
    description: currentTranslation.description,
    buttonName: currentTranslation.buttonName,
    translationsMap,
    buttonUrl: task.buttonUrl ?? '',
    tags: task.tags ?? [],
    isForFreeTrial: task.isForFreeTrial ?? false,
    visibilityRulesEnabled: task.visibilityGroup?.enabled ?? false,
    matchCondition:
      task.visibilityGroup?.condition ?? AdminTaskVisibilityGroupCondition.AND,
    visibilityRules:
      task.visibilityRules?.map((rule) => ({
        id: rule.id,
        product: rule.product,
        filterBy: rule.filterBy,
        operator: rule.operator,
        status: rule.status,
        dateValue: rule.dateValue,
      })) ?? [],
    featureFlagKey: task.featureFlagKey ?? null,
  };
};

export const formStateToAdminTask = (
  values: EditableTaskFormState,
): AdminTaskPayload => {
  const mergedMap = {
    ...values.translationsMap,
    [values.taskLanguage]: {
      title: values.title,
      description: values.description,
      buttonName: values.buttonName,
    },
  };

  const translations = Object.entries(mergedMap).map(([lang, fields]) => ({
    language: lang as LanguagesEnum,
    name: fields.title,
    description: fields.description,
    buttonName: fields.buttonName,
  }));

  return {
    ...(values.taskId.startsWith('temp-') ? {} : { id: values.taskId }),
    order: values.order,
    active: true,
    buttonUrl: values.buttonUrl || null,
    isForFreeTrial: values.isForFreeTrial,
    tags: values.tags.map((tag) => tag.id),
    visibilityGroup: {
      enabled: values.visibilityRulesEnabled,
      condition: values.matchCondition,
    },
    translations,
    visibilityRules: values.visibilityRules.map((rule) => ({
      id: rule.id,
      product: rule.product,
      filterBy: rule.filterBy,
      operator: rule.operator,
      status: rule.status,
      dateValue: rule.dateValue,
    })),
    featureFlagKey: values.featureFlagKey || null,
  };
};

export const isTaskVisibleByFeatureFlag = (
  task: AccountAdminTask,
  accountCanUseFeature: (
    feature: Subjects | PostHogFeatureFlags,
    featureFlagAbilities: any,
  ) => boolean,
  featureFlagAbilities: any,
): boolean => {
  const featureFlag = task.task.featureFlagKey;
  if (!featureFlag) return true;
  return accountCanUseFeature(
    featureFlag as PostHogFeatureFlags,
    featureFlagAbilities,
  );
};

export const getTaskLanguageOptions = () =>
  availableLanguagesDropdown.map((lang) => ({
    id: lang.value,
    label: lang.label,
    value: lang.value,
  }));

export const mapAdminTaskTagsToOptions = (
  tags: Tag[] | undefined,
  language: LanguagesEnum,
  t: TranslationFunctionType,
): AdminTaskTagOption[] => {
  const dynamicOptions =
    !tags || !Array.isArray(tags)
      ? []
      : tags
          .map((tag) => {
            const translation =
              tag.translations?.find((tr) => tr.language === language) ??
              tag.translations?.find(
                (tr) => tr.language === LanguagesEnum.EN,
              ) ??
              tag.translations?.[0];

            if (!translation || !tag.id) {
              return null;
            }

            return {
              id: tag.id,
              value: tag.id,
              label: translation.name,
            };
          })
          .filter((option): option is AdminTaskTagOption => option !== null);

  return [
    {
      id: AdminTaskTagEnum.NONE,
      value: AdminTaskTagEnum.NONE,
      label: t('admin.tasks.form.options.tag.none'),
    },
    ...dynamicOptions,
  ];
};

export const getCategoryOptions = (t: TranslationFunctionType) => [
  {
    id: 'paying-customer',
    label: t('admin.tasks.form.options.category.payingCustomer'),
    value: false,
  },
  {
    id: 'free-trial',
    label: t('admin.tasks.form.options.category.freeCustomer'),
    value: true,
  },
];

export const getMatchConditionOptions = (t: TranslationFunctionType) => [
  {
    id: AdminTaskVisibilityGroupCondition.AND,
    label: t('admin.tasks.form.options.matchCondition.and'),
    value: AdminTaskVisibilityGroupCondition.AND,
  },
  {
    id: AdminTaskVisibilityGroupCondition.OR,
    label: t('admin.tasks.form.options.matchCondition.or'),
    value: AdminTaskVisibilityGroupCondition.OR,
  },
];

export const getFilterByOptions = (t: TranslationFunctionType) => [
  {
    id: AdminTaskRuleFilterBy.STATUS,
    label: t('admin.tasks.form.options.filterBy.status'),
    value: AdminTaskRuleFilterBy.STATUS,
  },
  {
    id: AdminTaskRuleFilterBy.ONBOARDING_DATE,
    label: t('admin.tasks.form.options.filterBy.onboardingDate'),
    value: AdminTaskRuleFilterBy.ONBOARDING_DATE,
  },
  {
    id: AdminTaskRuleFilterBy.ACCOUNT_CREATION_DATE,
    label: t('admin.tasks.form.options.filterBy.creationDate'),
    value: AdminTaskRuleFilterBy.ACCOUNT_CREATION_DATE,
  },
];

export const getOperatorOptions = (
  filterBy: AdminTaskRuleFilterBy,
  t: TranslationFunctionType,
) => {
  if (
    filterBy === AdminTaskRuleFilterBy.ONBOARDING_DATE ||
    filterBy === AdminTaskRuleFilterBy.ACCOUNT_CREATION_DATE
  ) {
    return [
      {
        id: AdminTaskRuleOperator.AFTER,
        label: t('admin.tasks.form.options.operator.after'),
        value: AdminTaskRuleOperator.AFTER,
      },
      {
        id: AdminTaskRuleOperator.BEFORE,
        label: t('admin.tasks.form.options.operator.before'),
        value: AdminTaskRuleOperator.BEFORE,
      },
    ];
  }

  return [
    {
      id: AdminTaskRuleOperator.IS,
      label: t('admin.tasks.form.options.operator.is'),
      value: AdminTaskRuleOperator.IS,
    },
    {
      id: AdminTaskRuleOperator.IS_NOT,
      label: t('admin.tasks.form.options.operator.isNot'),
      value: AdminTaskRuleOperator.IS_NOT,
    },
  ];
};

export const getProductOptions = (
  t: TranslationFunctionType,
  filterBy?: AdminTaskRuleFilterBy,
) => {
  if (filterBy === AdminTaskRuleFilterBy.ACCOUNT_CREATION_DATE) {
    return [
      {
        id: AdminTaskRuleProduct.ACCOUNT,
        label: t('admin.tasks.form.options.product.account'),
        value: AdminTaskRuleProduct.ACCOUNT,
      },
    ];
  }

  return [
    {
      id: AdminTaskRuleProduct.AWT,
      label: t('admin.tasks.form.options.product.awt'),
      value: AdminTaskRuleProduct.AWT,
    },
    {
      id: AdminTaskRuleProduct.PHISHING,
      label: t('admin.tasks.form.options.product.phishing'),
      value: AdminTaskRuleProduct.PHISHING,
    },
    {
      id: AdminTaskRuleProduct.SECURITY_CULTURE,
      label: t('admin.tasks.form.options.product.securityCulture'),
      value: AdminTaskRuleProduct.SECURITY_CULTURE,
    },
    {
      id: AdminTaskRuleProduct.AUTO_PHISHING,
      label: t('admin.tasks.form.options.product.autoPhishing'),
      value: AdminTaskRuleProduct.AUTO_PHISHING,
    },
    {
      id: AdminTaskRuleProduct.PHISHING_ONE_OFF,
      label: t('admin.tasks.form.options.product.phishingOneOff'),
      value: AdminTaskRuleProduct.PHISHING_ONE_OFF,
    },
    {
      id: AdminTaskRuleProduct.FREE_BASELINE_MEASUREMENT,
      label: t('admin.tasks.form.options.product.freeBaselineMeasurement'),
      value: AdminTaskRuleProduct.FREE_BASELINE_MEASUREMENT,
    },
  ];
};

export const getStatusOptions = (t: TranslationFunctionType) => [
  {
    id: AdminTaskRuleAccountStatus.ONBOARDING,
    label: t('admin.tasks.form.options.status.onboarding'),
    value: AdminTaskRuleAccountStatus.ONBOARDING,
  },
  {
    id: AdminTaskRuleAccountStatus.OFFBOARDING,
    label: t('admin.tasks.form.options.status.offboarding'),
    value: AdminTaskRuleAccountStatus.OFFBOARDING,
  },
  {
    id: AdminTaskRuleAccountStatus.ACTIVE,
    label: t('admin.tasks.form.options.status.active'),
    value: AdminTaskRuleAccountStatus.ACTIVE,
  },
  {
    id: AdminTaskRuleAccountStatus.INACTIVE,
    label: t('admin.tasks.form.options.status.inactive'),
    value: AdminTaskRuleAccountStatus.INACTIVE,
  },
  {
    id: AdminTaskRuleAccountStatus.FREE_TRIAL,
    label: t('admin.tasks.form.options.status.freeTrial'),
    value: AdminTaskRuleAccountStatus.FREE_TRIAL,
  },
];
